import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Grid } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";

import AddIcon from "../../../../components/icon/icons/layer-22-icon.component";
import { PullRight } from "../../../../components/layout/pull-right/pull-right.styles";
import { AddProductModal } from "../../../../features/products/features/add-product-modal/components/add-product-modal/add-product-modal.component";
import { ProductsTable } from "../../../../features/products/components/products-table/products-table.component";
import usePermissions from "../../../../hooks/usePermissions";
import { OutlinedButton } from "../../../../components/form-controls/button/outlined-button/outlined-button.component";
import * as campaignBriefStore from "../../../../pages/campaign-brief/store/campaign-brief.store";
import { canDownloadProductsDetails } from "../../../../types/product/ibrief-product/ibrief-product.functions";
import { FileDownload } from "../../../../features/file-attachments/file-download/components/file-download/file-download-component";
import { IProductDetail } from "../../../../types/product/iproduct-detail.interface";
import { BasicResourceType } from "../../../../types/resources/resources.enums";
import { ProductHistory } from "../../../../features/product-history/components/product-history.component";
import { ErrorBox } from "../../../../components/error-box/error-box.component";
import { createFormRef } from "../../../../features/forms/types/forms.functions";
import { SpinnerLoader } from "../../../../components/loaders";
import { GreenButton } from "../../../../components/form-controls/button/green-button/green-button.component";
import { BriefProductsForm } from "../../../../features/products/types/brief-product-form/brief-product-form.interface";
import { dateIsBeforeToday } from "../../../../utils/date-format.utils";

import * as campaignBriefProductsStore from "../../features/campaign-brief-products/store/campaign-brief-products.store";
import { campaignBriefProductsFileDownloadEndPoint } from "../../features/campaign-brief-products/services/campaign-brief-products.service";

import { CampaignBriefProductsTabProps } from "./campaign-brief-products-tab.props";
import { useCampaignBriefProductsTabStyles } from "./campaign-brief-products-tab.styles";
import { HiddenHeading } from "../../../pages.styles";

export const CampaignBriefProductsTab = (props: CampaignBriefProductsTabProps) => {
  const userPermissions = usePermissions();
  const formRef = useRef<any>(createFormRef());

  useEffect(() => {
    campaignBriefProductsStore.fetchProducts(props.briefId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errors = useSelector(campaignBriefProductsStore.getErrors);
  const products = useSelector(campaignBriefProductsStore.getProducts);
  const isLoading = useSelector(campaignBriefProductsStore.isLoading);
  const classes = useCampaignBriefProductsTabStyles();

  const openProductModal = () => campaignBriefStore.openAddProductsModal();

  const onAddProduct = (product: IProductDetail) =>
    campaignBriefProductsStore.addProduct(product.id, props.briefId);

  const onClose = () => campaignBriefStore.closeAddProductsModal();

  const onSubmit = (form: BriefProductsForm) => {
    campaignBriefProductsStore.updateProducts(form.products, props.briefId);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <HiddenHeading>{props.pageTitle}</HiddenHeading>
        <PullRight>
          <SpinnerLoader showIf={isLoading} inline size={25} />
          <GreenButton
            disabled={isLoading}
            marginAround
            onClick={() => formRef.current.handleSubmit(onSubmit)()}
          >
            Save
          </GreenButton>
          <ProductHistory
            showIf={!!products.length}
            buttonText="Show History"
            buttonSize="small"
            className={classes.marginRight}
            customComponent={OutlinedButton}
            resourceId={props.briefId}
            resourceType={BasicResourceType.CampaignBrief}
          />
          <FileDownload
            variant="button"
            authRequired={true}
            icon={<GetAppIcon />}
            url={campaignBriefProductsFileDownloadEndPoint(props.briefId)}
            showIf={canDownloadProductsDetails(products, userPermissions)}
            saveAsFileName="campaign-briefs-products.csv"
          >
            Export CSV
          </FileDownload>
        </PullRight>
        <ErrorBox
          fullHeight={false}
          message={errors?.errorMessage!}
          showIf={useSelector(campaignBriefProductsStore.hasErrors)}
        />
        <ProductsTable
          showIf={!!products.length}
          briefEndDatePassed={dateIsBeforeToday(props.endDate ?? "")}
          formRef={formRef}
          pageType="campaign-briefs"
          products={products}
          resourceType={BasicResourceType.CampaignBriefProducts}
          setProductsFormIsDirty={props.setProductsFormIsDirty}
        />

        <OutlinedButton
          disabled={!userPermissions.manageProducts}
          onClick={openProductModal}
          variant={"outlined"}
          startIcon={<AddIcon />}
        >
          Add Product
        </OutlinedButton>
      </Grid>
      <AddProductModal
        pageType="campaign-briefs"
        products={products}
        isOpen={useSelector(campaignBriefStore.isProductModalOpen)}
        onAddProduct={onAddProduct}
        onClose={onClose}
      />
    </Grid>
  );
};
